/**
 * CreativeAnalysisPanel — right pane of the Creative Workspace split.
 * Tab bar over four analysis tabs: Performance / Persona Fit / Formula /
 * Intelligence.
 */

import { clsx } from 'clsx';
import type {
  AnalysisTab,
  CreativeSiblingSummary,
  CreativeWorkspaceData,
  PDOFitConcept,
  WorkspaceBriefAngle,
} from './types';
import { PerformanceTab } from './tabs/PerformanceTab';
import { PersonaFitTab } from './tabs/PersonaFitTab';
import { FormulaTab } from './tabs/FormulaTab';
import { IntelligenceTab } from './tabs/IntelligenceTab';

interface Props {
  data: CreativeWorkspaceData;
  siblings: CreativeSiblingSummary[];
  activePersona: string | null;
  onPersonaChange: (id: string) => void;
  activeTab: AnalysisTab;
  onTabChange: (tab: AnalysisTab) => void;
  linkedAngle?: WorkspaceBriefAngle | null;
  onApplyToBrief?: (mod: Record<string, unknown>) => void;
  onTestVariant?: (mod: Record<string, unknown>, index: number) => void;
  onGapClick?: (concept: PDOFitConcept) => void;
}

const TABS: Array<{ key: AnalysisTab; label: string }> = [
  { key: 'performance', label: 'Performance' },
  { key: 'persona_fit', label: 'Persona Fit' },
  { key: 'formula', label: 'Formula' },
  { key: 'intelligence', label: 'Intelligence' },
];

export function CreativeAnalysisPanel({
  data,
  siblings,
  activePersona,
  onPersonaChange,
  activeTab,
  onTabChange,
  linkedAngle,
  onApplyToBrief,
  onTestVariant,
  onGapClick,
}: Props) {
  const creativeRecord = data.creative as Record<string, unknown>;
  const creativeHeadline = ((creativeRecord.form as { headline?: string } | undefined)?.headline ??
    '') as string;

  return (
    <div className="flex flex-col h-full">
      <nav className="flex border-b border-slate-200 bg-white sticky top-0 z-[1]">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => onTabChange(tab.key)}
            className={clsx(
              'flex-1 px-4 py-3 text-[11px] font-mono uppercase tracking-wider transition whitespace-nowrap',
              activeTab === tab.key
                ? 'text-teal-600 border-b-2 border-teal-500 bg-white'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50',
            )}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="flex-1 min-h-0">
        {activeTab === 'performance' && (
          <PerformanceTab data={data} siblings={siblings} />
        )}
        {activeTab === 'persona_fit' && (
          <PersonaFitTab
            reviewSummaries={data.review_summaries ?? {}}
            latestReview={data.latest_review ?? null}
            linkedConcepts={data.linked_concepts ?? []}
            linkedAngle={linkedAngle ?? null}
            briefId={data.brief?.brief_id ?? null}
            activePersona={activePersona}
            onPersonaChange={onPersonaChange}
            onGapClick={onGapClick}
            creativeHeadline={creativeHeadline}
          />
        )}
        {activeTab === 'formula' && (
          <FormulaTab
            data={data}
            imageUrl={data.best_media_url ?? (creativeRecord.form as { image_url?: string; thumbnail_url?: string } | undefined)?.image_url ?? (creativeRecord.form as { thumbnail_url?: string } | undefined)?.thumbnail_url ?? null}
          />
        )}
        {activeTab === 'intelligence' && (
          <IntelligenceTab
            data={data}
            onApplyToBrief={onApplyToBrief}
            onTestVariant={onTestVariant}
          />
        )}
      </div>
    </div>
  );
}
